import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import {
    buildPageContextFields,
    buildPromptFields,
    buildLyraAdviceFields,
    buildSentimentChipFields,
    buildShadeFinderProductFields,
    buildShadeFinderShadeFields,
    withPromptSource
} from 'ai/analytics/utils/sotFields';
import { EVENT_NAMES } from 'ai/analytics/events';

/**
 * Reports an entry-point prompt click. `source` suffixes sotV402 with the prompt
 * origin (GAI-7300) — see {@link withPromptSource}.
 *
 * Scoped to this event only: follow-up clicks keep a bare sotV402, so the suffix
 * is applied here rather than inside `buildPromptFields`, which both events share.
 *
 * @param {object} params
 * @param {string} params.promptText raw prompt text
 * @param {object} [params.pageContext] placement context (PLP / PDP / SRP)
 * @param {string} [params.source] prompt origin from the backend (`static` | `dynamic`)
 */
export function trackPromptClick({ promptText, pageContext = {}, source }) {
    if (!promptText) {
        return;
    }

    const eventName = EVENT_NAMES.PROMPT_CLICK;
    const sotFields = {
        ...buildPageContextFields(pageContext),
        ...buildPromptFields(withPromptSource(promptText, source)),
        ...buildLyraAdviceFields()
    };

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        },
        finishEventWithoutTimeout: true
    });
}

export function trackFollowUpClick({ followUpText, pageContext = {}, messageId, previousMessageId }) {
    if (!followUpText) {
        return;
    }

    const eventName = EVENT_NAMES.FOLLOW_UP_CLICK;
    const sotFields = {
        ...buildPageContextFields(pageContext),
        ...buildPromptFields(followUpText, messageId, previousMessageId),
        ...buildLyraAdviceFields()
    };

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        }
    });
}

export function trackSentimentChipClick({ value, position, type, pageContext = {} }) {
    if (!value || !position) {
        return;
    }

    const eventName = EVENT_NAMES.SENTIMENT_CHIP_CLICK;
    const sotFields = {
        ...buildPageContextFields(pageContext),
        ...buildSentimentChipFields({ value, position, type }),
        ...buildLyraAdviceFields()
    };

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        }
    });

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
            actionInfo: `${anaConsts.SUPER_CHAT.SUPERCHAT_REVIEW_SENTIMENTS}:${value}`
        }
    });
}

export function trackShadeFinderProductSelected({
    sessionId, messageId, productId, productName, brandName
}) {
    const eventName = EVENT_NAMES.SHADE_FINDER_PRODUCT_SELECTED;
    const sotFields = buildShadeFinderProductFields({
        sessionId,
        messageId,
        productId,
        productName,
        brandName
    });

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        }
    });
}

export function trackShadeFinderShadeSelected({
    sessionId, messageId, skuId, shadeCode, productId
}) {
    const eventName = EVENT_NAMES.SHADE_FINDER_SHADE_SELECTED;
    const sotFields = buildShadeFinderShadeFields({
        sessionId,
        messageId,
        skuId,
        shadeCode,
        productId
    });

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        }
    });
}

export function trackSignInCTAClick({ pageContext = {} }) {
    const eventName = EVENT_NAMES.SIGN_IN_CTA_CLICK;
    const sotFields = {
        ...buildPageContextFields(pageContext),
        ...buildLyraAdviceFields()
    };

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType: eventName,
            ...sotFields
        }
    });
}
